import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Plus, X } from "lucide-react";

import { useModalContext } from "../../components/ModalProvider";
import {
  DocumentClassification,
  DocumentConfusable,
  DocumentRole,
  useDocumentAnalysisAPI,
} from "../../DocumentAnalysisAPI";
import { isKnownClass, ontologyClassNames, validateRole } from "./ontologyValidation";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

/**
 * Editing one role of the catalogue.
 *
 * Section by section, in the order the table shows them, and everything editable at once with a
 * single save — the sections are read together and are usually changed together, and a save per
 * section would write a generation per section for what is one revision.
 *
 * Validation is shown while typing and never blocks. A term whose class does not exist yet is a
 * perfectly reasonable thing to write down: the class may be added tomorrow, and the note is what
 * makes that visible. The panel marks; it does not refuse.
 */

const STATE_OPERATORS = ["create", "amend", "terminate", "attest"];
const SHARPNESS = ["amtlich", "formal", "frei"];
// "parameter" is what the seed writes and "param" what the panel first offered — both occur in the
// catalogue, and a value the list does not contain would leave the dropdown showing nothing.
const SUBTYPE_KINDS = ["parameter", "param", "operator"];

interface Properties {
  role: DocumentRole;
  /** Names of the roles currently in the catalogue — what a target-role reference may point at. */
  knownRoles: Set<string>;
  onSaved: () => Promise<void> | void;
}

/** A class name with an autocomplete over the ontology, marked when it names nothing. */
function ClassNameInput({
  value,
  onChange,
  placeholder,
}: {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const classNames = useMemo(() => ontologyClassNames(), []);
  const listId = useMemo(() => `ontologyClasses-${Math.random().toString(36).slice(2)}`, []);
  const unknown = !!value && !isKnownClass(value);

  return (
    <div className="flex min-w-[160px] flex-col">
      <Input
        list={listId}
        value={value ?? ""}
        placeholder={placeholder ?? t("documentAnalysisPanel.editor.classPlaceholder")}
        className={unknown ? "border-destructive" : undefined}
        // An empty field is "no class yet", which the catalogue records as null rather than "".
        onChange={(event) => onChange(event.target.value.trim() === "" ? null : event.target.value)}
      />
      <datalist id={listId}>
        {classNames.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
      {unknown ? (
        <span className="mt-0.5 flex items-center gap-1 text-xs text-destructive">
          <AlertTriangle className="h-3 w-3" aria-hidden />
          {t("documentAnalysisPanel.editor.unknownClass", { className: value })}
        </span>
      ) : null}
    </div>
  );
}

function Section({ title, onAdd, children }: { title: string; onAdd: () => void; children: React.ReactNode }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">{title}</h4>
        <Button variant="ghost" size="sm" onClick={onAdd}>
          <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
          {t("documentAnalysisPanel.editor.add")}
        </Button>
      </div>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}

function RemoveButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label={label} onClick={onClick}>
      <X className="h-4 w-4" aria-hidden />
    </Button>
  );
}

/**
 * A list of cues, as chips.
 *
 * Chips rather than a textarea of comma-separated words, because each entry is a phrase in its own
 * right — several of them contain commas ("Alle übrigen Bestimmungen bleiben unberührt"), and a
 * separator that occurs inside the values is not a separator. Adding is Enter or the button;
 * removing is the × on the chip.
 */
function CueList({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");

  const add = () => {
    const value = draft.trim();
    // Silently ignoring a duplicate beats an error message: the operator meant to have it in the
    // list, and it is.
    if (value && values.indexOf(value) < 0) onChange([...values, value]);
    setDraft("");
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1">
        {values.length === 0 ? (
          <span className="text-xs text-muted-foreground">{t("documentAnalysisPanel.editor.noCues")}</span>
        ) : (
          values.map((value, index) => (
            <span
              key={`${value}-${index}`}
              className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-0.5 text-xs"
            >
              {value}
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive"
                aria-label={t("documentAnalysisPanel.editor.removeCue", { cue: value })}
                onClick={() => onChange(values.filter((_entry, position) => position !== index))}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))
        )}
      </div>

      <div className="flex gap-2">
        <Input
          value={draft}
          placeholder={placeholder ?? t("documentAnalysisPanel.editor.cuePlaceholder")}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            // The editor sits inside a table; a stray Enter must not travel up and do something
            // else on the way.
            event.preventDefault();
            add();
          }}
        />
        <Button variant="outline" size="sm" disabled={draft.trim().length === 0} onClick={add}>
          <Plus className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

export default function DocumentRoleEditor({ role, knownRoles, onSaved }: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const api = useDocumentAnalysisAPI();

  const [draft, setDraft] = useState<DocumentRole>(role);
  const [saving, setSaving] = useState(false);

  // A save writes a new generation and the table reloads; the draft follows what came back rather
  // than keeping what was typed, so what is on screen is what is on record.
  useEffect(() => setDraft(role), [role]);

  const validation = useMemo(() => validateRole(draft, knownRoles), [draft, knownRoles]);

  const patch = (changes: Partial<DocumentRole>) => setDraft((current) => ({ ...current, ...changes }));

  const list = <T,>(key: keyof DocumentRole): T[] => ((draft[key] as any[]) ?? []) as T[];
  const setList = (key: keyof DocumentRole, values: any[]) => patch({ [key]: values } as Partial<DocumentRole>);
  const update = (key: keyof DocumentRole, index: number, changes: Record<string, any>) =>
    setList(
      key,
      list<any>(key).map((entry, position) => (position === index ? { ...entry, ...changes } : entry)),
    );
  const remove = (key: keyof DocumentRole, index: number) =>
    setList(
      key,
      list<any>(key).filter((_entry, position) => position !== index),
    );

  const save = async () => {
    setSaving(true);
    try {
      await api.saveDocumentRole(role.name, {
        category: draft.category,
        classification: draft.classification ?? {},
        refCodes: draft.refCodes ?? [],
        subtypes: draft.subtypes ?? [],
        stateOperatorDefault: draft.stateOperatorDefault ?? null,
        backRefs: draft.backRefs ?? [],
        expectationEdges: draft.expectationEdges ?? [],
        primaryEntities: draft.primaryEntities ?? [],
        referenceEntities: draft.referenceEntities ?? [],
        relations: draft.relations ?? [],
        keyNote: draft.keyNote,
        openWorldNote: draft.openWorldNote,
        clusterRole: draft.clusterRole,
      });

      await onSaved();
    } catch (error) {
      pushError(error);
    } finally {
      setSaving(false);
    }
  };

  // `Array.from` rather than a spread: the build targets ES5, where a Set is not iterable.
  const roleOptions = useMemo(
    () => Array.from(knownRoles).sort((left, right) => left.localeCompare(right)),
    [knownRoles],
  );

  /** A target role: pick an existing one, or write a name that does not exist yet. */
  const targetRoleInput = (value: string, onChange: (value: string) => void) => {
    const listId = `documentRoles-${role.name}`;
    const dangling = !!value && !knownRoles.has(value);

    return (
      <div className="flex min-w-[180px] flex-col">
        <Input
          list={listId}
          value={value}
          className={dangling ? "border-destructive" : undefined}
          onChange={(event) => onChange(event.target.value)}
        />
        <datalist id={listId}>
          {roleOptions.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
        {dangling ? (
          <span className="mt-0.5 flex items-center gap-1 text-xs text-destructive">
            <AlertTriangle className="h-3 w-3" aria-hidden />
            {t("documentAnalysisPanel.editor.unknownRole", { role: value })}
          </span>
        ) : null}
      </div>
    );
  };

  const classification = draft.classification ?? {};
  const patchClassification = (changes: Partial<DocumentClassification>) =>
    patch({ classification: { ...classification, ...changes } });

  const confusable = classification.confusableWith ?? [];
  const updateConfusable = (index: number, changes: Partial<DocumentConfusable>) =>
    patchClassification({
      confusableWith: confusable.map((entry, position) =>
        position === index ? { ...entry, ...changes } : entry,
      ),
    });

  return (
    <div className="flex flex-col gap-6 py-4">
      {/* First, because it is what a classification run reads. The sections below describe what the
          document *carries*; this one describes how it is *recognised*. */}
      <div className="rounded-md border bg-background p-4">
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <h4 className="text-sm font-semibold">{t("documentAnalysisPanel.classification.title")}</h4>
          <span className="text-xs text-muted-foreground">
            {t("documentAnalysisPanel.classification.explanation")}
          </span>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="lg:col-span-2">
            <Label htmlFor={`${role.name}-classificationDescription`}>
              {t("documentAnalysisPanel.classification.description")}
            </Label>
            <Textarea
              id={`${role.name}-classificationDescription`}
              rows={3}
              value={classification.description ?? ""}
              placeholder={t("documentAnalysisPanel.classification.descriptionPlaceholder")}
              onChange={(event) => patchClassification({ description: event.target.value })}
            />
          </div>

          <div>
            <Label>{t("documentAnalysisPanel.classification.positiveCues")}</Label>
            <CueList
              values={classification.positiveCues ?? []}
              onChange={(positiveCues) => patchClassification({ positiveCues })}
              placeholder={t("documentAnalysisPanel.classification.positiveCuesPlaceholder")}
            />
          </div>

          <div>
            <Label>{t("documentAnalysisPanel.classification.structureCues")}</Label>
            <CueList
              values={classification.structureCues ?? []}
              onChange={(structureCues) => patchClassification({ structureCues })}
              placeholder={t("documentAnalysisPanel.classification.structureCuesPlaceholder")}
            />
          </div>

          <div className="lg:col-span-2">
            <Section
              title={t("documentAnalysisPanel.classification.confusableWith")}
              onAdd={() =>
                patchClassification({ confusableWith: [...confusable, { role: "", discriminator: "" }] })
              }
            >
              {confusable.map((entry, index) => (
                <div key={index} className="flex flex-wrap items-start gap-2">
                  {targetRoleInput(entry.role ?? "", (value) => updateConfusable(index, { role: value }))}
                  <Input
                    className="min-w-[240px] flex-1"
                    value={entry.discriminator ?? ""}
                    placeholder={t("documentAnalysisPanel.classification.discriminatorPlaceholder")}
                    onChange={(event) => updateConfusable(index, { discriminator: event.target.value })}
                  />
                  <RemoveButton
                    label={t("documentAnalysisPanel.editor.remove")}
                    onClick={() =>
                      patchClassification({
                        confusableWith: confusable.filter((_entry, position) => position !== index),
                      })
                    }
                  />
                </div>
              ))}
            </Section>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section
          title={t("documentAnalysisPanel.columns.subtypes")}
          onAdd={() => setList("subtypes", [...list("subtypes"), { name: "", kind: "param" }])}
        >
          {list<any>("subtypes").map((subtype, index) => (
            <div key={index} className="flex flex-col gap-2 rounded-md border p-2">
            <div className="flex flex-wrap items-start gap-2">
              <Input
                className="min-w-[160px] flex-1"
                value={subtype.name ?? ""}
                onChange={(event) => update("subtypes", index, { name: event.target.value })}
              />
              <Select value={subtype.kind ?? "param"} onValueChange={(kind) => update("subtypes", index, { kind })}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUBTYPE_KINDS.map((kind) => (
                    <SelectItem key={kind} value={kind}>
                      {kind}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {subtype.kind === "operator" ? (
                <>
                  <Select
                    value={subtype.stateOperator ?? "create"}
                    onValueChange={(stateOperator) => update("subtypes", index, { stateOperator })}
                  >
                    <SelectTrigger className="w-[130px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATE_OPERATORS.map((operator) => (
                        <SelectItem key={operator} value={operator}>
                          {operator}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <label className="flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={!!subtype.exTunc}
                      onChange={(event) => update("subtypes", index, { exTunc: event.target.checked })}
                    />
                    ex tunc
                  </label>
                </>
              ) : null}

              <RemoveButton
                label={t("documentAnalysisPanel.editor.remove")}
                onClick={() => remove("subtypes", index)}
              />
            </div>

            {/* The subtype's own classification data. A subtype is chosen only once its role has
                been recognised, so what it needs is a description that separates it from its
                siblings — not from every document there is. */}
            <Textarea
              rows={2}
              value={subtype.classification?.description ?? ""}
              placeholder={
                /^x/.test(subtype.name ?? "")
                  ? t("documentAnalysisPanel.classification.axisPlaceholder")
                  : t("documentAnalysisPanel.classification.subtypePlaceholder")
              }
              onChange={(event) =>
                update("subtypes", index, {
                  classification: { ...(subtype.classification ?? {}), description: event.target.value },
                })
              }
            />
            <CueList
              values={subtype.classification?.cues ?? []}
              onChange={(cues) =>
                update("subtypes", index, { classification: { ...(subtype.classification ?? {}), cues } })
              }
            />
            </div>
          ))}
        </Section>

        <Section
          title={t("documentAnalysisPanel.columns.refCodes")}
          onAdd={() => setList("refCodes", [...list("refCodes"), { source: "gifDR", code: "", status: "unverified" }])}
        >
          {list<any>("refCodes").map((refCode, index) => (
            <div key={index} className="flex flex-wrap items-center gap-2">
              <Input
                className="w-[120px]"
                value={refCode.source ?? ""}
                onChange={(event) => update("refCodes", index, { source: event.target.value })}
              />
              <Input
                className="min-w-[140px] flex-1"
                value={refCode.code ?? ""}
                placeholder="—"
                onChange={(event) => update("refCodes", index, { code: event.target.value || null })}
              />
              {/* Verification is a person's statement that this code was checked against gif or
                  GEFMA — hence a switch, not something derived. */}
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={refCode.status === "verified"}
                  onChange={(event) =>
                    update("refCodes", index, { status: event.target.checked ? "verified" : "unverified" })
                  }
                />
                {t("documentAnalysisPanel.editor.verified")}
              </label>
              <RemoveButton
                label={t("documentAnalysisPanel.editor.remove")}
                onClick={() => remove("refCodes", index)}
              />
            </div>
          ))}
        </Section>

        <Section
          title={t("documentAnalysisPanel.columns.backRefs")}
          onAdd={() => setList("backRefs", [...list("backRefs"), { targetRole: "", keySharpness: "frei" }])}
        >
          {list<any>("backRefs").map((reference, index) => (
            <div key={index} className="flex flex-wrap items-start gap-2">
              {targetRoleInput(reference.targetRole ?? "", (targetRole) =>
                update("backRefs", index, { targetRole }),
              )}
              <Select
                value={reference.keySharpness ?? "frei"}
                onValueChange={(keySharpness) => update("backRefs", index, { keySharpness })}
              >
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SHARPNESS.map((entry) => (
                    <SelectItem key={entry} value={entry}>
                      {entry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="min-w-[160px] flex-1"
                value={reference.note ?? ""}
                placeholder={t("documentAnalysisPanel.editor.note")}
                onChange={(event) => update("backRefs", index, { note: event.target.value })}
              />
              <RemoveButton
                label={t("documentAnalysisPanel.editor.remove")}
                onClick={() => remove("backRefs", index)}
              />
            </div>
          ))}
        </Section>

        <Section
          title={t("documentAnalysisPanel.columns.expectationEdges")}
          onAdd={() =>
            setList("expectationEdges", [...list("expectationEdges"), { targetRole: "", trigger: "", cadenceSource: "" }])
          }
        >
          {list<any>("expectationEdges").map((edge, index) => (
            <div key={index} className="flex flex-wrap items-start gap-2">
              {targetRoleInput(edge.targetRole ?? "", (targetRole) =>
                update("expectationEdges", index, { targetRole }),
              )}
              <Input
                className="min-w-[140px] flex-1"
                value={edge.trigger ?? ""}
                placeholder={t("documentAnalysisPanel.editor.trigger")}
                onChange={(event) => update("expectationEdges", index, { trigger: event.target.value })}
              />
              <Input
                className="min-w-[140px] flex-1"
                value={edge.cadenceSource ?? ""}
                placeholder={t("documentAnalysisPanel.editor.cadence")}
                onChange={(event) => update("expectationEdges", index, { cadenceSource: event.target.value })}
              />
              <RemoveButton
                label={t("documentAnalysisPanel.editor.remove")}
                onClick={() => remove("expectationEdges", index)}
              />
            </div>
          ))}
        </Section>

        {(["primaryEntities", "referenceEntities"] as const).map((key) => (
          <Section
            key={key}
            title={t(`documentAnalysisPanel.columns.${key}`)}
            onAdd={() => setList(key, [...list(key), { term: "", className: null }])}
          >
            {list<any>(key).map((entity, index) => (
              <div key={index} className="flex flex-wrap items-start gap-2">
                <Input
                  className="min-w-[140px] flex-1"
                  value={entity.term ?? ""}
                  placeholder={t("documentAnalysisPanel.editor.term")}
                  onChange={(event) => update(key, index, { term: event.target.value })}
                />
                <ClassNameInput
                  value={entity.className}
                  onChange={(className) => update(key, index, { className })}
                />
                <RemoveButton label={t("documentAnalysisPanel.editor.remove")} onClick={() => remove(key, index)} />
              </div>
            ))}
          </Section>
        ))}
      </div>

      <Section
        title={t("documentAnalysisPanel.columns.relations")}
        onAdd={() =>
          setList("relations", [
            ...list("relations"),
            { sourceTerm: "", sourceClassName: null, name: "", targetTerm: "", targetClassName: null },
          ])
        }
      >
        {list<any>("relations").map((relation, index) => (
          <div key={index} className="flex flex-wrap items-start gap-2">
            <Input
              className="min-w-[120px]"
              value={relation.sourceTerm ?? ""}
              placeholder={t("documentAnalysisPanel.editor.source")}
              onChange={(event) => update("relations", index, { sourceTerm: event.target.value })}
            />
            <ClassNameInput
              value={relation.sourceClassName}
              onChange={(sourceClassName) => update("relations", index, { sourceClassName })}
            />
            <Input
              className="min-w-[120px]"
              value={relation.name ?? ""}
              placeholder={t("documentAnalysisPanel.editor.relationName")}
              onChange={(event) => update("relations", index, { name: event.target.value })}
            />
            <Input
              className="min-w-[120px]"
              value={relation.targetTerm ?? ""}
              placeholder={t("documentAnalysisPanel.editor.target")}
              onChange={(event) => update("relations", index, { targetTerm: event.target.value })}
            />
            <ClassNameInput
              value={relation.targetClassName}
              onChange={(targetClassName) => update("relations", index, { targetClassName })}
            />
            {validation.has("relations", index) ? (
              <span className="flex items-center gap-1 text-xs text-destructive">
                <AlertTriangle className="h-3 w-3" aria-hidden />
                {validation.has("relations", index)?.message}
              </span>
            ) : null}
            <RemoveButton
              label={t("documentAnalysisPanel.editor.remove")}
              onClick={() => remove("relations", index)}
            />
          </div>
        ))}
      </Section>

      <div className="grid gap-4 lg:grid-cols-3">
        {(["keyNote", "openWorldNote", "clusterRole"] as const).map((key) => (
          <div key={key}>
            <Label htmlFor={`${role.name}-${key}`}>{t(`documentAnalysisPanel.editor.${key}`)}</Label>
            <Textarea
              id={`${role.name}-${key}`}
              rows={2}
              value={(draft[key] as string) ?? ""}
              onChange={(event) => patch({ [key]: event.target.value } as Partial<DocumentRole>)}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Button disabled={saving} onClick={save}>
          {saving ? t("documentAnalysisPanel.editor.saving") : t("global.save")}
        </Button>
        <Button variant="outline" disabled={saving} onClick={() => setDraft(role)}>
          {t("global.cancel")}
        </Button>

        <span className="text-xs text-muted-foreground">
          {t("documentAnalysisPanel.editor.generation", {
            generation: role.generation ?? 1,
            editedBy: role.provenance?.editedBy ?? role.provenance?.source ?? "—",
          })}
        </span>

        {validation.invalidCount > 0 ? (
          <span className="flex items-center gap-1 text-xs text-destructive">
            <AlertTriangle className="h-3 w-3" aria-hidden />
            {t("documentAnalysisPanel.editor.stillInvalid", { count: validation.invalidCount })}
          </span>
        ) : null}
      </div>
    </div>
  );
}
